// notification-bell.component.ts - FIXED VERSION
import {
  Component, OnInit, OnDestroy, HostListener,
  ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, inject
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationItem, NotificationService } from '../services/notification.service';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../shared/auth/auth.service';

@Component({
  selector: 'app-notification-bell',
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.scss'],
  imports: [MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  isOpen = false;
  isLoading = false;
  unreadCount = 0;
  notifications: NotificationItem[] = [];
  isShaking = false;
  private audio: HTMLAudioElement;
  private destroy$ = new Subject<void>();
  private shakeTimeout: ReturnType<typeof setTimeout> | null = null;
  private auth = inject(AuthService);

  constructor(
    private notifService: NotificationService,
    private router: Router,
    private elRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {
    this.audio = new Audio('assets/sounds/notification.mp3');
    this.audio.volume = 0.8;

    if (this.audio.canPlayType('audio/mpeg')) {
      this.audio.src = 'assets/sounds/notification.mp3';
    } else if (this.audio.canPlayType('audio/ogg')) {
      this.audio.src = 'assets/sounds/notification.ogg';
    }
  }

  ngOnInit(): void {
    // ✅ FIXED: Subscribe to auth service to get current role
    this.auth.user
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: any) => {
        if (user?.role) {
          // ✅ Load unread count for the active role
          this.notifService.loadUnreadCountForRole(user.role);
        }
      });

    // ❌ REMOVED: Don't call the generic loadUnreadCount()
    // this.notifService.loadUnreadCount();

    // Subscribe to unread count updates
    this.notifService.unreadCount
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.unreadCount = count;
        this.cdr.markForCheck();
      });

    // Subscribe to notifications list updates
    this.notifService.notifications
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.notifications = items.slice(0, 5); // Show only first 5
        this.cdr.markForCheck();
      });

    // Shake bell AND play sound on new notification
    this.notifService.newNotification
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.triggerShake();
        this.playNotificationSound();
      });
  }

  /**
   * Play notification sound
   */
  private playNotificationSound(): void {
    this.audio.currentTime = 0;
    this.audio.play().catch(err => {
      console.warn('Failed to play notification sound:', err);
    });
  }

  /**
   * Trigger shake animation
   */
  private triggerShake(): void {
    this.isShaking = true;
    this.cdr.markForCheck();

    if (this.shakeTimeout) clearTimeout(this.shakeTimeout);

    this.shakeTimeout = setTimeout(() => {
      this.isShaking = false;
      this.cdr.markForCheck();
    }, 800);
  }

  /**
   * Toggle dropdown visibility
   */
  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.loadNotifications();
    }
  }

  /**
   * Close dropdown
   */
  closeDropdown(): void {
    this.isOpen = false;
  }

  /**
   * Load recent notifications for current role
   */
  private loadNotifications(): void {
    const currentUser = this.auth.user.getValue();
    if (!currentUser?.role) return;

    this.isLoading = true;
    this.cdr.markForCheck();

    this.notifService.loadRecentNotificationsByRole(currentUser.role, 5, 0)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Failed to load notifications', err);
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Handle notification click - mark as read and navigate if link available
   */
onNotificationClick(notif: NotificationItem): void {
  if (!notif.isRead) {
    this.notifService.markAsRead(notif.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => console.error('Failed to mark as read', err)
      });
  }
  
  // ✅ Handle Event notifications — navigate with eventId query param
  if (notif.triggeredByResourceType === 'Event' && notif.triggeredByResourceId) {
    this.router.navigate(['/events'], {
      queryParams: { eventId: notif.triggeredByResourceId }
    });
  }
  // ✅ Handle ServiceRequest notifications — navigate with serviceRequestId query param
  else if (notif.triggeredByResourceType === 'ServiceRequest' && notif.triggeredByResourceId) {
    this.router.navigate(['/app/service-requests'], {
      queryParams: { serviceRequestId: notif.triggeredByResourceId }
    });
  }
  
  this.closeDropdown();
}

  /**
   * Mark all notifications as read
   */
  onMarkAllRead(event: Event): void {
    event.stopPropagation();

    this.notifService.markAllAsRead()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => console.error('Failed to mark all as read', err)
      });
  }

  /**
   * Delete a single notification
   */
  onDelete(event: Event, notifId: string): void {
    event.stopPropagation();

    this.notifService.deleteNotification(notifId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // ✅ Reload to fill the gap with the next notification
          this.loadNotifications();
        },
        error: (err) => console.error('Failed to delete notification', err)
      });
  }

  /**
   * Navigate to notifications page
   */
  viewAll(): void {
    this.router.navigate(['/app/notifications']);
    this.closeDropdown();
  }

  /**
   * Get Material icon for notification type
   */
  getNotifIcon(type: number): string {
    const icons: Record<number, string> = {
      1: 'science', 2: 'biotech', 3: 'check_circle',
      10: 'medication', 11: 'local_pharmacy', 12: 'verified',
      20: 'event', 21: 'event_busy', 22: 'update', 23: 'alarm',
      30: 'celebration', 31: 'edit_calendar', 32: 'cancel',
      40: 'build', 41: 'task_alt', 42: 'done_all',
      50: 'how_to_reg', 51: 'person_off', 52: 'archive', 53: 'restore',
      100: 'warning'
    };
    return icons[type] ?? 'notifications';
  }

  /**
   * Get color class for notification type
   */
  getNotifColor(type: number): string {
    if (type >= 1 && type <= 3) return 'lab';
    if (type >= 10 && type <= 12) return 'med';
    if (type >= 20 && type <= 23) return 'apt';
    if (type >= 30 && type <= 32) return 'evt';
    if (type >= 40 && type <= 42) return 'svc';
    if (type >= 50 && type <= 53) return 'usr';
    return 'sys';
  }

  /**
   * Format notification time in human-readable format
   */
  formatTime(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  /**
   * Get badge label with 99+ limit
   */
  get badgeLabel(): string {
    return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
  }

  /**
   * Close dropdown when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!this.elRef.nativeElement.contains(target)) {
      this.isOpen = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Close dropdown on Escape key
   */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.isOpen = false;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.shakeTimeout) {
      clearTimeout(this.shakeTimeout);
    }
  }
}